import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { useToastStore } from '../store/toastStore';
import { scheduleAPI } from '../api/client';
import { track } from '../utils/analytics';
import useSnapshotData from '../hooks/useSnapshotData';
import Layout from '../components/Layout';
import BusSeatLayout from '../components/BusSeatLayout';
import BottomSheetSelect from '../components/BottomSheetSelect';
import { COMMERCE_ENABLED } from '../config/features';

const MIN_SEAT_COUNT = 5;
const ADD_SEAT_COUNT = 3;

const WEEKDAYS = [
  { value: 'monday', label: '월', full: '월요일' },
  { value: 'tuesday', label: '화', full: '화요일' },
  { value: 'wednesday', label: '수', full: '수요일' },
  { value: 'thursday', label: '목', full: '목요일' },
  { value: 'friday', label: '금', full: '금요일' },
];

const DAY_FULL = Object.fromEntries(WEEKDAYS.map(d => [d.value, d.full]));

const emptyConfig = () => ({
  lineGroupSeq: '', routeName: '', busTime: '', busSeq: '',
  stopName: '', stopSeq: '', skipped: false,
});

// 진행률 바
function ProgressBar({ current, total, label }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-sub-text font-medium">{label}</span>
        <span className="text-xs text-sub-text">{current}/{total}</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-tayo rounded-full transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function ScheduleBulkForm() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const showToast = useToastStore((s) => s.showToast);
  const queryClient = useQueryClient();
  const userId = user?.userId;

  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState(null);

  // Step 1: 요일 복수 선택
  const [selectedDays, setSelectedDays] = useState([]);

  // 요일별 등교/하교 설정
  const [dayConfigs, setDayConfigs] = useState({});

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  const hasAccess = !COMMERCE_ENABLED || (user?.accessUntil && new Date(user.accessUntil) > new Date());
  if (!hasAccess) {
    return (
      <Layout title="일괄 등록" backTo="/schedules" showBottomNav>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mb-5">
            <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-charcoal mb-2">타요패스가 필요해요</h3>
          <p className="text-sm text-sub-text mb-6 leading-relaxed">자동 예약 스케줄을 등록하려면<br />타요패스가 필요합니다</p>
          <button onClick={() => navigate('/pricing')} className="btn-press px-6 py-3 bg-gradient-to-r from-tayo to-tayo-dark text-white font-bold rounded-2xl">
            요금제 보기
          </button>
        </div>
      </Layout>
    );
  }

  // ====== Step 계산 ======
  const dayStepCount = selectedDays.length * 2;
  const seatStep = 2 + dayStepCount;
  const confirmStep = seatStep + 1;
  const totalSteps = confirmStep;

  const getDayDirection = (step) => {
    if (step < 2 || step >= seatStep) return null;
    const idx = step - 2;
    const dayIdx = Math.floor(idx / 2);
    const direction = idx % 2 === 0 ? 'UP' : 'DOWN';
    return { day: selectedDays[dayIdx], dayIdx, direction };
  };

  const currentDayDir = getDayDirection(currentStep);

  // ====== 공통 훅 사용 ======
  const { routes, timetable, stops, isLoading: loadingSnapshot } = useSnapshotData({
    dayOfWeek: currentDayDir?.day || 'monday',
    direction: currentDayDir?.direction || 'UP',
    lineGroupSeq: currentDayDir ? dayConfigs[currentDayDir.day]?.[currentDayDir.direction]?.lineGroupSeq : '',
  });

  // 좌석 (기존 스케줄의 가장 최근 값을 기본값으로)
  const { data: existingSchedules } = useQuery({
    queryKey: ['schedules', userId],
    queryFn: async () => {
      const result = await scheduleAPI.list(userId);
      return result.success ? result.data || [] : [];
    },
    enabled: !!userId,
    staleTime: Infinity,
  });

  // 기존 스케줄 맵 (day+direction → schedule)
  const existingMap = useMemo(() => {
    const map = {};
    (existingSchedules || []).forEach(s => {
      const key = `${s.day_of_week}|${s.direction}`;
      map[key] = s;
    });
    return map;
  }, [existingSchedules]);

  const defaultSeatPreferences = useMemo(() => {
    if (!existingSchedules?.length) return [];
    const sorted = [...existingSchedules].sort((a, b) =>
      new Date(b.created_at) - new Date(a.created_at)
    );
    return sorted[0]?.seat_preferences || [];
  }, [existingSchedules]);

  const [seatPreferences, setSeatPreferences] = useState([]);

  useEffect(() => {
    if (defaultSeatPreferences.length > 0 && seatPreferences.length === 0) {
      setSeatPreferences(defaultSeatPreferences);
    }
  }, [defaultSeatPreferences]);

  const handleSeatClick = useCallback((seatNumber) => {
    setSeatPreferences(prev =>
      prev.includes(seatNumber) ? prev.filter(s => s !== seatNumber) : [...prev, seatNumber]
    );
  }, []);

  const clearAll = useCallback(() => setSeatPreferences([]), []);

  // 현재 스텝의 config
  const currentConfig = currentDayDir ? dayConfigs[currentDayDir.day]?.[currentDayDir.direction] : null;

  // ====== 캐리오버: 다음 요일 진입 시 이전 값 복사 ======
  const applyCarryover = (dayIdx, direction) => {
    const day = selectedDays[dayIdx];
    const current = dayConfigs[day]?.[direction];
    if (!current || current.lineGroupSeq || current.skipped) return;
    for (let i = dayIdx - 1; i >= 0; i--) {
      const prev = dayConfigs[selectedDays[i]]?.[direction];
      if (prev && !prev.skipped && prev.lineGroupSeq) {
        setDayConfigs(prev => ({
          ...prev,
          [day]: { ...prev[day], [direction]: { ...prev[selectedDays[i]][direction] } },
        }));
        return;
      }
    }
  };

  useEffect(() => {
    const dd = getDayDirection(currentStep);
    if (dd && dd.dayIdx > 0) {
      applyCarryover(dd.dayIdx, dd.direction);
    }
  }, [currentStep]);

  const [seatWarning, setSeatWarning] = useState(null);

  // ====== 저장 mutation ======
  const saveMutation = useMutation({
    mutationFn: async (schedules) => {
      const result = await scheduleAPI.createBulk(schedules);
      if (!result.success) {
        const err = new Error(result.error || '일괄 등록 실패');
        err.needMore = result.needMore;
        err.totalAllocated = result.totalAllocated;
        throw err;
      }
      return result;
    },
    onSuccess: (_, schedules) => {
      queryClient.invalidateQueries({ queryKey: ['schedules', userId] });
      const count = Object.values(dayConfigs).reduce((sum, dc) => {
        if (dc.UP && !dc.UP.skipped && dc.UP.lineGroupSeq) sum++;
        if (dc.DOWN && !dc.DOWN.skipped && dc.DOWN.lineGroupSeq) sum++;
        return sum;
      }, 0);
      track('schedule-created', { isBulk: true, count: schedules.length });
      showToast(`${count}개의 스케줄이 등록되었습니다`, 'success');
      navigate('/schedules');
    },
    onError: (err) => {
      if (err.needMore) {
        setSeatWarning({
          message: err.message || '선택한 좌석이 모두 인기가 많아요. 추가로 3개 이상 좌석을 선택해주세요.',
          totalAllocated: err.totalAllocated,
        });
        // 좌석 선택 단계로 이동
        setCurrentStep(seatStep);
      } else {
        setError(err.message || '일괄 등록 실패');
      }
    },
  });

  // ====== 핸들러 ======
  const toggleDay = (day) => {
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const toggleAllDays = () => {
    setSelectedDays(prev => prev.length === 5 ? [] : WEEKDAYS.map(d => d.value));
  };

  const updateCurrentConfig = (updates) => {
    if (!currentDayDir) return;
    setDayConfigs(prev => ({
      ...prev,
      [currentDayDir.day]: {
        ...prev[currentDayDir.day],
        [currentDayDir.direction]: {
          ...prev[currentDayDir.day][currentDayDir.direction],
          ...updates,
        },
      },
    }));
  };

  const handleRouteChange = (e) => {
    const selected = routes.find(r => r.seq === e.target.value);
    if (selected) {
      updateCurrentConfig({
        lineGroupSeq: selected.seq, routeName: selected.name,
        busTime: '', busSeq: '', stopName: '', stopSeq: '',
      });
    }
  };

  const handleBusChange = (time) => {
    const bus = timetable.find(b => b.time === time);
    if (bus) updateCurrentConfig({ busTime: time, busSeq: bus.busSeq });
  };

  const handleStopChange = (e) => {
    const selected = stops.find(s => s.seq.toString() === e.target.value);
    if (selected) updateCurrentConfig({ stopSeq: selected.seq.toString(), stopName: selected.name });
  };

  const handleSkip = () => {
    updateCurrentConfig({ ...emptyConfig(), skipped: true });
    goNext();
  };

  const goNext = () => setCurrentStep(prev => prev + 1);

  const finishDaySelection = () => {
    const sorted = WEEKDAYS.map(d => d.value).filter(d => selectedDays.includes(d));
    setSelectedDays(sorted);
    const configs = {};
    sorted.forEach(day => {
      configs[day] = { UP: emptyConfig(), DOWN: emptyConfig() };
    });
    setDayConfigs(configs);
    setCurrentStep(2);
  };

  const minSeatRequired = seatWarning ? seatPreferences.length + ADD_SEAT_COUNT : MIN_SEAT_COUNT;

  const handleSubmit = () => {
    if (seatPreferences.length < minSeatRequired) {
      showToast(`선호 좌석을 ${minSeatRequired}개 이상 선택해주세요`, 'error');
      return;
    }
    const schedules = [];
    selectedDays.forEach(day => {
      ['UP', 'DOWN'].forEach(dir => {
        const cfg = dayConfigs[day]?.[dir];
        if (cfg && !cfg.skipped && cfg.lineGroupSeq && cfg.busTime && cfg.stopSeq) {
          schedules.push({
            userId, dayOfWeek: day, direction: dir,
            routeName: cfg.routeName, lineGroupSeq: cfg.lineGroupSeq,
            busTime: cfg.busTime, busSeq: cfg.busSeq,
            stopName: cfg.stopName, stopSeq: cfg.stopSeq,
            seatPreferences,
          });
        }
      });
    });
    if (schedules.length === 0) {
      showToast('등록할 스케줄이 없습니다', 'error');
      return;
    }
    setError(null);
    setSeatWarning(null);
    saveMutation.mutate(schedules);
  };

  // ====== 뒤로가기 ======
  useEffect(() => {
    if (currentStep > 1) history.pushState({ step: currentStep }, '');
    const handlePopState = () => {
      if (currentStep > 1) setCurrentStep(prev => Math.max(1, prev - 1));
      else navigate('/schedules');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentStep, navigate]);

  const handleBack = () => {
    if (currentStep > 1) history.back();
    else navigate('/schedules');
  };

  if (!user) return null;

  // ====== 스텝별 진행 조건 ======
  const canProceedDayDir = () => {
    if (!currentConfig) return false;
    if (currentConfig.skipped) return true;
    return !!(currentConfig.lineGroupSeq && currentConfig.busTime && currentConfig.stopSeq);
  };

  const getStepLabel = () => {
    if (currentStep === 1) return '요일 선택';
    if (currentDayDir) {
      const dirLabel = currentDayDir.direction === 'UP' ? '등교' : '하교';
      return `${DAY_FULL[currentDayDir.day]} ${dirLabel}`;
    }
    if (currentStep === seatStep) return '선호 좌석';
    if (currentStep === confirmStep) return '최종 확인';
    return '';
  };

  const scheduleCount = selectedDays.reduce((sum, day) => {
    ['UP', 'DOWN'].forEach(dir => {
      const cfg = dayConfigs[day]?.[dir];
      if (cfg && !cfg.skipped && cfg.lineGroupSeq) sum++;
    });
    return sum;
  }, 0);

  // ====== 새로 등록할 스케줄이 없으면 좌석 스텝 건너뛰기 ======
  useEffect(() => {
    if (currentStep === seatStep && scheduleCount === 0) {
      setCurrentStep(confirmStep);
    }
  }, [currentStep, seatStep, scheduleCount, confirmStep]);

  return (
    <Layout
      title="일괄 등록"
      onBack={handleBack}
      backLabel="뒤로"
      showBottomNav={false}
      maxWidth="lg"
    >
      {/* 진행률 바 */}
      <ProgressBar current={currentStep} total={totalSteps} label={getStepLabel()} />

      {/* 에러 메시지 */}
      {error && (
        <div className="mb-4 bg-coral/10 border border-coral/30 rounded-xl p-4 animate-fade-up">
          <p className="text-sm text-coral font-medium">{error}</p>
        </div>
      )}

      {/* ============ Step 1: 요일 복수 선택 ============ */}
      {currentStep === 1 && (
        <div className="animate-fade-up space-y-6">
          <h2 className="text-xl font-bold text-charcoal">무슨 요일에 탈 건가요?</h2>

          <button
            type="button"
            onClick={toggleAllDays}
            className={`w-full py-3 rounded-xl text-sm font-semibold transition-all duration-200 border-2 btn-press ${
              selectedDays.length === 5
                ? 'bg-tayo-light border-tayo text-tayo'
                : 'bg-white border-gray-200 text-charcoal hover:border-gray-300'
            }`}
          >
            {selectedDays.length === 5 ? '전체 해제' : '월~금 전체 선택'}
          </button>

          <div className="flex gap-3 justify-center">
            {WEEKDAYS.map((day) => (
              <button
                key={day.value}
                type="button"
                onClick={() => toggleDay(day.value)}
                className={`w-14 h-14 rounded-2xl text-base font-bold transition-all duration-200 btn-press ${
                  selectedDays.includes(day.value)
                    ? 'bg-tayo text-white shadow-md shadow-tayo/30'
                    : 'bg-gray-100 text-charcoal hover:bg-gray-200'
                }`}
              >
                {day.label}
              </button>
            ))}
          </div>

          <p className="text-xs text-sub-text text-center">
            {selectedDays.length > 0 ? `${selectedDays.length}개 요일 선택됨` : '등록할 요일을 선택해주세요'}
          </p>

          <button
            type="button"
            disabled={selectedDays.length === 0}
            onClick={finishDaySelection}
            className="w-full py-4 rounded-xl text-white font-bold text-base transition-all duration-200 btn-press bg-gradient-to-r from-tayo to-tayo-dark disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed shadow-lg shadow-tayo/20 disabled:shadow-none"
          >
            다음
          </button>
        </div>
      )}

      {/* ============ 요일별 등교/하교 설정 ============ */}
      {currentDayDir && (() => {
        const existingKey = `${currentDayDir.day}|${currentDayDir.direction}`;
        const existingSchedule = existingMap[existingKey];

        // 기존 스케줄이 있으면 스킵 안내
        if (existingSchedule && !currentConfig?.lineGroupSeq && !currentConfig?.skipped) {
          return (
            <div className="animate-fade-up space-y-5" key={`${currentDayDir.day}-${currentDayDir.direction}-existing`}>
              <h2 className="text-xl font-bold text-charcoal">
                {DAY_FULL[currentDayDir.day]}{' '}
                <span className={currentDayDir.direction === 'UP' ? 'text-tayo' : 'text-orange-500'}>
                  {currentDayDir.direction === 'UP' ? '등교' : '하교'}
                </span>
              </h2>

              <div className="bg-tayo-light/40 border border-tayo/20 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-tayo" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm font-bold text-tayo">이미 설정된 스케줄이 있어요</span>
                </div>
                <p className="text-sm font-semibold text-charcoal">{existingSchedule.route_name}</p>
                <p className="text-xs text-sub-text mt-1">{existingSchedule.bus_time} · {existingSchedule.stop_name}</p>
                {existingSchedule.seat_preferences?.length > 0 && (
                  <p className="text-xs text-sub-text mt-0.5">선호좌석: {existingSchedule.seat_preferences.join(', ')}</p>
                )}
              </div>

              <button
                type="button"
                onClick={() => { handleSkip(); }}
                className="w-full py-4 rounded-xl text-white font-bold text-base transition-all duration-200 btn-press bg-gradient-to-r from-tayo to-tayo-dark shadow-lg shadow-tayo/20"
              >
                다음으로
              </button>
            </div>
          );
        }

        return (
        <div className="animate-fade-up space-y-5" key={`${currentDayDir.day}-${currentDayDir.direction}`}>
          <div>
            <h2 className="text-xl font-bold text-charcoal">
              {DAY_FULL[currentDayDir.day]}{' '}
              <span className={currentDayDir.direction === 'UP' ? 'text-tayo' : 'text-orange-500'}>
                {currentDayDir.direction === 'UP' ? '등교' : '하교'}
              </span>
            </h2>
            {currentDayDir.dayIdx > 0 && currentConfig?.lineGroupSeq && !currentConfig?.skipped && (
              <p className="text-xs text-sub-text mt-1">
                이전 요일 설정이 적용되어 있습니다. 변경이 필요한 부분만 수정하세요.
              </p>
            )}
          </div>

          {/* 노선 선택 */}
          <div>
            <label className="block text-sm font-semibold text-charcoal mb-3">노선</label>
            {loadingSnapshot ? (
              <div className="bg-white rounded-xl p-4 text-center">
                <div className="inline-block w-5 h-5 border-2 border-tayo border-t-transparent rounded-full animate-spin mr-2" />
                <span className="text-sm text-sub-text">노선 정보 불러오는 중...</span>
              </div>
            ) : routes.length === 0 ? (
              <div className="bg-white rounded-xl p-4 text-center">
                <p className="text-sm text-sub-text">노선 데이터가 없습니다</p>
              </div>
            ) : (
              <BottomSheetSelect
                value={currentConfig?.lineGroupSeq || ''}
                onChange={handleRouteChange}
                options={routes.map(r => ({ value: r.seq, label: r.name, sublabel: `${r.busCnt}대` }))}
                placeholder="노선을 선택하세요"
                title={`${currentDayDir.direction === 'UP' ? '등교' : '하교'} 노선 선택`}
                searchable={routes.length > 5}
              />
            )}
          </div>

          {/* 시간 선택 */}
          {currentConfig?.lineGroupSeq && (
            <div>
              <label className="block text-sm font-semibold text-charcoal mb-3">버스 시간</label>
              {timetable.length === 0 ? (
                <div className="bg-white rounded-xl p-4 text-center">
                  <p className="text-sm text-sub-text">시간표 데이터가 없습니다</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[28vh] overflow-y-auto pr-1">
                  {timetable.map((bus, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleBusChange(bus.time)}
                      className={`w-full py-3.5 px-4 rounded-xl text-left transition-all duration-200 border-2 btn-press ${
                        currentConfig.busTime === bus.time
                          ? 'bg-tayo-light border-tayo text-tayo font-bold'
                          : 'bg-white border-gray-100 text-charcoal hover:border-gray-200'
                      }`}
                    >
                      <span className="text-base font-semibold">{bus.time}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 정류장 선택 */}
          {currentConfig?.lineGroupSeq && stops.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-charcoal mb-3">정류장</label>
              <BottomSheetSelect
                value={currentConfig?.stopSeq || ''}
                onChange={handleStopChange}
                options={stops.map(s => ({ value: s.seq, label: s.name }))}
                placeholder="정류장을 선택하세요"
                title={`${currentDayDir.direction === 'UP' ? '등교' : '하교'} 정류장 선택`}
                searchable={stops.length > 5}
              />
            </div>
          )}

          {/* 버튼 영역 */}
          <div className="space-y-3">
            <button
              type="button"
              disabled={!canProceedDayDir()}
              onClick={goNext}
              className="w-full py-4 rounded-xl text-white font-bold text-base transition-all duration-200 btn-press bg-gradient-to-r from-tayo to-tayo-dark disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed shadow-lg shadow-tayo/20 disabled:shadow-none"
            >
              다음
            </button>
            <button
              type="button"
              onClick={handleSkip}
              className="w-full py-3 rounded-xl text-sub-text font-semibold text-sm bg-gray-50 hover:bg-gray-100 transition-colors btn-press"
            >
              {currentDayDir.direction === 'UP' ? '등교' : '하교'} 안 탐 (건너뛰기)
            </button>
          </div>
        </div>
        );
      })()}

      {/* ============ 좌석 선택 ============ */}
      {currentStep === seatStep && (
        <div className="animate-fade-up space-y-6">
          <h2 className="text-xl font-bold text-charcoal">선호 좌석을 선택해주세요</h2>

          {/* 좌석 선점 경고 */}
          {seatWarning && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 animate-fade-up">
              <p className="text-sm font-semibold text-amber-800">{seatWarning.message}</p>
              <p className="text-xs text-amber-600 mt-1">
                예약 성공률을 높이려면 넉넉하게 선택해주세요.
              </p>
            </div>
          )}

          {seatWarning?.totalAllocated >= 30 && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3">
              <p className="text-xs font-medium text-rose-700">
                이 버스는 예약 경쟁이 치열해요 ({seatWarning.totalAllocated}/44석)
              </p>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-charcoal">
                선호 좌석
                <span className="text-xs text-sub-text font-normal ml-1.5">
                  ({seatPreferences.length}개 선택 · 최소 {minSeatRequired}개)
                </span>
              </label>
              {seatPreferences.length > 0 && (
                <button
                  type="button"
                  onClick={() => { clearAll(); setSeatWarning(null); }}
                  className="text-xs text-coral font-medium hover:text-coral/80 transition-colors"
                >
                  전체 해제
                </button>
              )}
            </div>
            <BusSeatLayout
              mode="preference"
              seatPreferences={seatPreferences}
              onSeatClick={(seat) => { handleSeatClick(seat); setSeatWarning(null); }}
            />
            {seatPreferences.length > 0 && (
              <p className="mt-3 text-xs text-sub-text text-center">
                우선순위: {seatPreferences.join(', ')}
              </p>
            )}
            {seatPreferences.length > 0 && seatPreferences.length < minSeatRequired && (
              <p className="mt-2 text-xs text-amber-600 text-center font-medium">
                {minSeatRequired - seatPreferences.length}개 더 선택해주세요
              </p>
            )}
          </div>

          <button
            type="button"
            disabled={seatPreferences.length < minSeatRequired}
            onClick={goNext}
            className="w-full py-4 rounded-xl text-white font-bold text-base transition-all duration-200 btn-press bg-gradient-to-r from-tayo to-tayo-dark disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed shadow-lg shadow-tayo/20 disabled:shadow-none"
          >
            다음
          </button>
        </div>
      )}

      {/* ============ 확인 + 저장 ============ */}
      {currentStep === confirmStep && (
        <div className="animate-fade-up space-y-4">
          <h2 className="text-xl font-bold text-charcoal">등록할 스케줄을 확인해주세요</h2>

          {selectedDays.map((day) => {
            const upCfg = dayConfigs[day]?.UP;
            const downCfg = dayConfigs[day]?.DOWN;

            return (
              <div key={day} className="bg-white rounded-2xl shadow-card border border-border overflow-hidden">
                <div className="bg-gray-50 px-4 py-2.5 border-b border-border">
                  <span className="text-sm font-bold text-charcoal">{DAY_FULL[day]}</span>
                </div>
                <div className="grid grid-cols-2 divide-x divide-border">
                  {/* 등교 */}
                  <div className="p-3.5">
                    {upCfg?.skipped ? (
                      <div className="flex items-center justify-center h-full min-h-[48px]">
                        <span className="text-xs text-sub-text">건너뜀</span>
                      </div>
                    ) : upCfg?.lineGroupSeq ? (
                      <div>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <span className="text-[10px] font-bold text-tayo bg-tayo-light px-1.5 py-0.5 rounded">등교</span>
                        </div>
                        <p className="text-sm font-semibold text-charcoal">{upCfg.routeName}</p>
                        <p className="text-xs text-sub-text mt-0.5">{upCfg.busTime} · {upCfg.stopName}</p>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full min-h-[48px]">
                        <span className="text-xs text-sub-text">--</span>
                      </div>
                    )}
                  </div>
                  {/* 하교 */}
                  <div className="p-3.5">
                    {downCfg?.skipped ? (
                      <div className="flex items-center justify-center h-full min-h-[48px]">
                        <span className="text-xs text-sub-text">건너뜀</span>
                      </div>
                    ) : downCfg?.lineGroupSeq ? (
                      <div>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">하교</span>
                        </div>
                        <p className="text-sm font-semibold text-charcoal">{downCfg.routeName}</p>
                        <p className="text-xs text-sub-text mt-0.5">{downCfg.busTime} · {downCfg.stopName}</p>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full min-h-[48px]">
                        <span className="text-xs text-sub-text">--</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* 좌석 정보 (새 스케줄이 있을 때만) */}
          {scheduleCount > 0 && (
            <div className="bg-gray-50 rounded-xl p-4">
              <span className="text-xs text-sub-text">선호좌석: </span>
              <span className="text-sm font-medium text-charcoal">{seatPreferences.join(', ')}</span>
            </div>
          )}

          {scheduleCount > 0 ? (
            <>
              <p className="text-xs text-sub-text text-center">
                총 {scheduleCount}개의 스케줄이 등록됩니다
              </p>
              <button
                type="button"
                disabled={saveMutation.isPending}
                onClick={handleSubmit}
                className="w-full py-4 rounded-xl text-white font-bold text-base transition-all duration-200 btn-press bg-gradient-to-r from-tayo to-tayo-dark disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed shadow-lg shadow-tayo/20 disabled:shadow-none"
              >
                {saveMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    등록 중...
                  </span>
                ) : `${scheduleCount}개 스케줄 일괄 등록`}
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-sub-text text-center">
                새로 등록할 스케줄이 없습니다
              </p>
              <button
                type="button"
                onClick={() => navigate('/schedules')}
                className="w-full py-4 rounded-xl text-white font-bold text-base transition-all duration-200 btn-press bg-gradient-to-r from-tayo to-tayo-dark shadow-lg shadow-tayo/20"
              >
                완료
              </button>
            </>
          )}
        </div>
      )}
    </Layout>
  );
}
